import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { PORTAL_POPOVER_PRIORITY } from '@rsos/capstone/src/constants/portalPriorities';
import Portal from '../../Portal';

interface RelativeProps {
  cssDisplayProperty?: string;
}

const Relative = styled.div<RelativeProps>`
  position: relative;
  display: ${({ cssDisplayProperty }) =>
    cssDisplayProperty ? cssDisplayProperty : 'inline-flex'};
`;

interface PopoverContentProps {
  top?: number;
  left?: number;
  zIndex?: number;
}

const PopoverContent = styled.div<PopoverContentProps>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  z-index: ${({ top, left, zIndex = 3 }) => (top && left ? zIndex : -1)};
`;

interface TriggerWrapperProps {
  cssDisplayProperty?: string;
}

const TriggerWrapper = styled.div<TriggerWrapperProps>`
  display: ${({ cssDisplayProperty }) =>
    cssDisplayProperty ? cssDisplayProperty : 'inline-flex'};
`;

/**
 * Props for the Popover component
 */
interface PopoverProps {
  /** Element that triggers the popover */
  trigger: ReactNode;
  /** Popover element */
  children: ReactNode;
  /** Toggles the visibility of the popover */
  isOpen: boolean;
  /** X offset from the element (default: 0) */
  xOffset?: number;
  /** Y offset from the element (default: 0) */
  yOffset?: number;
  /** Popover ID attribute */
  popoverID: string;
  /**
   * Should the popover display in a specific offset from the trigger element
   * (default: false)
   */
  useCustomOffset?: boolean;
  /**
   * Display the popover content in the center x offset from the trigger element.
   * If this prop is used, then the `useCustomOffset` and `xOffset` props will be ignored.
   * (default: false)
   */
  useCenterXOffset?: boolean;
  /**
   * Display the popover content in the center y offset from the trigger element.
   * If this prop is used, then the `useCustomOffset` and `yOffset` props will be ignored.
   * (default: false)
   */
  useCenterYOffset?: boolean;
  /**
   * CSS display property to override the component's default inline-flex value.
   */
  cssDisplayProperty?: string;
  /**
   * Sets the z-index of the popover content.
   */
  zIndex?: number;
}

/**
 * Customizable Popover component
 */
const Popover = ({
  trigger: Trigger,
  children,
  isOpen,
  xOffset = 0,
  yOffset = 0,
  popoverID,
  useCustomOffset = false,
  useCenterXOffset = false,
  useCenterYOffset = false,
  cssDisplayProperty,
  zIndex = 3,
}: PopoverProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [boundingClient, setBoundingClient] = useState<DOMRect | undefined>();
  const [contentBoundingClient, setContentBoundingClient] = useState<
    DOMRect | undefined
  >();

  useEffect(() => {
    const rect = triggerRef?.current?.getBoundingClientRect();
    setBoundingClient(rect);

    const contentRect = contentRef?.current?.getBoundingClientRect();
    setContentBoundingClient(contentRect);
  }, [isOpen]);

  return (
    <Relative cssDisplayProperty={cssDisplayProperty}>
      <TriggerWrapper cssDisplayProperty={cssDisplayProperty} ref={triggerRef}>
        {Trigger}
      </TriggerWrapper>
      <Portal id={popoverID} priority={PORTAL_POPOVER_PRIORITY}>
        {isOpen && boundingClient && (
          <PopoverContent
            zIndex={zIndex}
            ref={contentRef}
            top={
              useCenterYOffset
                ? boundingClient.top + boundingClient.height / 2
                : useCustomOffset
                  ? boundingClient.top + yOffset
                  : boundingClient.top - yOffset + boundingClient.height / 2
            }
            left={
              useCenterXOffset
                ? boundingClient.left +
                  (boundingClient.width - (contentBoundingClient?.width || 0)) /
                    2
                : useCustomOffset
                  ? boundingClient.left + xOffset
                  : boundingClient.left + xOffset + boundingClient.width
            }
          >
            {children}
          </PopoverContent>
        )}
      </Portal>
    </Relative>
  );
};

Popover.propTypes = {
  trigger: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  xOffset: PropTypes.number,
  yOffset: PropTypes.number,
  popoverID: PropTypes.string.isRequired,
  useCustomOffset: PropTypes.bool,
  useCenterXOffset: PropTypes.bool,
  useCenterYOffset: PropTypes.bool,
  cssDisplayProperty: PropTypes.string,
  zIndex: PropTypes.number,
};

export default Popover;
